import { useState } from 'react';
import type { AiReportResult, SavedReportDto } from '@order-pilot/shared';
import { getErrorMessage } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { useDeleteSaved, useRunSaved, useSavedReports } from './api';

/** Saved reports open again on today's figures, free, on every plan. */
export function SavedPanel({ onOpen }: { onOpen: (title: string, result: AiReportResult) => void }) {
  const list = useSavedReports();
  const run = useRunSaved();
  const remove = useDeleteSaved();
  const [confirm, setConfirm] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const open = (r: SavedReportDto) => {
    setOpening(r.id);
    run.mutate(r.id, { onSuccess: (result) => onOpen(r.title, result), onSettled: () => setOpening(null) });
  };

  return (
    <section aria-labelledby="saved-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="saved-heading" className="text-sm font-semibold text-slate-900">
          Saved reports
        </h2>
        <p className="text-xs text-slate-500">Open again for free on today&apos;s data</p>
      </div>
      {list.isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-slate-200/80" />
      ) : list.isError ? (
        <p className="text-sm text-red-700">{getErrorMessage(list.error, 'Could not load saved reports.')}</p>
      ) : !list.data?.length ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500">
          Save an answer to keep it here. It opens again with the latest figures.
        </p>
      ) : (
        list.data.map((r) => (
          <div key={r.id} className="relative rounded-lg border border-slate-200 bg-white">
            <button type="button" onClick={() => open(r)} disabled={run.isPending} className="w-full p-3 pr-9 text-left hover:bg-slate-50">
              <div className="truncate text-sm font-medium text-slate-900">{r.title}</div>
              <div className="mt-0.5 text-xs text-slate-500">
                {opening === r.id ? 'Opening…' : `${r.lastRunAt ? 'Opened' : 'Saved'} ${formatRelative(r.lastRunAt ?? r.createdAt)}`}
              </div>
            </button>
            <button
              type="button"
              aria-label={`Delete ${r.title}`}
              onClick={() => setConfirm(r.id)}
              className="absolute right-2 top-2 rounded px-1.5 text-sm text-slate-400 hover:text-slate-900"
            >
              ×
            </button>
            {confirm === r.id ? (
              <div className="absolute inset-0 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
                <p className="flex-1 truncate text-xs text-slate-800">Delete “{r.title}”?</p>
                <button
                  type="button"
                  onClick={() => {
                    remove.mutate(r.id);
                    setConfirm(null);
                  }}
                  className="rounded-md bg-red-600 px-2.5 py-1 text-xs text-white"
                >
                  Delete
                </button>
                <button type="button" onClick={() => setConfirm(null)} className="rounded-md border border-slate-300 px-2.5 py-1 text-xs">
                  Keep
                </button>
              </div>
            ) : null}
          </div>
        ))
      )}
      {run.isError ? <p className="text-xs text-red-700">{getErrorMessage(run.error, 'Could not open this report.')}</p> : null}
    </section>
  );
}
